import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useDeactivateAccountMutation } from "../../store/api/authApi";
import s from "./section.module.css";
import cs from "./DangerModal.module.css";

const PauseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="10" y1="15" x2="10" y2="9" />
    <line x1="14" y1="15" x2="14" y2="9" />
  </svg>
);

export default function DeactivateAccount() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [deactivateAccount, { isLoading }] = useDeactivateAccountMutation();

  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  const handleConfirm = async () => {
    if (!password) {
      toast.error("Please enter your password to confirm.");
      return;
    }

    try {
      await deactivateAccount({ password }).unwrap();
      toast.success("Your account has been deactivated. You can reactivate it anytime by logging back in.");
      setOpen(false);
      logout();
      navigate("/");
    } catch (err) {
      const message =
        err?.data?.message ||
        Object.values(err?.data || {}).flat().join(" ") ||
        "Failed to deactivate account.";
      toast.error(message);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setPassword("");
    setShowPass(false);
  };

  return (
    <>
      {/* ── Row trigger ── */}
      <div className={cs.dangerRow}>
        <div className={cs.dangerRowLeft}>
          <span className={cs.dangerIcon} style={{ background: "#fff8ec", color: "#e67e22" }}>
            <PauseIcon />
          </span>
          <div>
            <div className={cs.dangerRowTitle}>Deactivate Account</div>
            <div className={cs.dangerRowSub}>
              Temporarily hide your profile and posts. You can reactivate anytime by logging back in.
            </div>
          </div>
        </div>
        <button
          className={cs.btnWarn}
          onClick={() => setOpen(true)}
        >
          Deactivate
        </button>
      </div>

      {/* ── Modal overlay ── */}
      {open && (
        <div className={cs.overlay} onClick={handleClose}>
          <div className={cs.modal} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className={cs.modalHeader} style={{ borderColor: "#f39c12" }}>
              <span className={cs.modalIconWrap} style={{ background: "#fff8ec", color: "#e67e22" }}>
                <PauseIcon />
              </span>
              <div>
                <h3 className={cs.modalTitle}>Deactivate your account?</h3>
                <p className={cs.modalSubtitle}>This will hide your profile until you log back in.</p>
              </div>
              <button className={cs.closeBtn} onClick={handleClose} aria-label="Close">✕</button>
            </div>

            {/* What happens */}
            <div className={cs.infoBox} style={{ background: "#fff8ec", borderColor: "#f9d49a" }}>
              <p className={cs.infoTitle}>What happens when you deactivate:</p>
              <ul className={cs.infoList}>
                <li>Your profile becomes invisible to others</li>
                <li>Your posts, likes and comments are hidden</li>
                <li>You won't receive notifications</li>
                <li>All your data is preserved — nothing is deleted</li>
                <li>Log back in at any time to reactivate instantly</li>
              </ul>
            </div>

            {/* Password confirm */}
            <div className={cs.fieldGroup}>
              <label className={cs.fieldLabel}>Confirm with your password</label>
              <div className={cs.inputWrap}>
                <input
                  className={cs.input}
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoFocus
                />
                <button
                  type="button"
                  className={cs.eyeBtn}
                  onClick={() => setShowPass((p) => !p)}
                >
                  {showPass ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className={cs.modalActions}>
              <button className={cs.btnCancel} onClick={handleClose}>
                Keep Account
              </button>
              <button
                className={cs.btnWarn}
                onClick={handleConfirm}
                disabled={isLoading || !password}
              >
                {isLoading ? "Deactivating…" : "Yes, Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}